/**
 * Normalización de unidades de ingredientes.
 *
 * Cada unidad reconocida pertenece a una FAMILIA con una unidad base:
 *   - mass   → gramos (g)
 *   - volume → mililitros (ml)
 *   - spoon_small / spoon_big → cucharaditas / cucharadas (no se convierten entre sí)
 *   - count  → unidades (ud); también la familia de los ingredientes sin unidad
 *
 * Solo se suman cantidades dentro de la MISMA familia; familias distintas para
 * un mismo ingrediente producen líneas separadas en la lista de compra.
 */

export type UnitFamily = "mass" | "volume" | "spoon_small" | "spoon_big" | "count" | "other";

export interface NormalizedUnit {
  family: UnitFamily;
  /** Factor a la unidad base de la familia (g, ml, cdta, cda, ud). */
  factor: number;
  /** Unidad original normalizada (lowercase, sin puntos). */
  raw: string;
}

const MASS: Record<string, number> = {
  g: 1,
  gr: 1,
  gramo: 1,
  gramos: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilo: 1000,
  kilos: 1000,
  kilogramo: 1000,
  kilogramos: 1000,
  mg: 0.001,
};

const VOLUME: Record<string, number> = {
  ml: 1,
  mililitro: 1,
  mililitros: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  litro: 1000,
  litros: 1000,
  liter: 1000,
  liters: 1000,
  taza: 250,
  tazas: 250,
  cup: 250,
  cups: 250,
};

const SPOON_SMALL = new Set(["cdta", "cdta.", "cucharadita", "cucharaditas", "tsp"]);
const SPOON_BIG = new Set(["cda", "cda.", "cucharada", "cucharadas", "tbsp"]);
const COUNT = new Set(["ud", "uds", "u", "unidad", "unidades", "pieza", "piezas", "pcs", "pc", ""]);

/** Normaliza el nombre de un ingrediente para usarlo como clave de agregación. */
export function normalizeIngredientName(name: string): string {
  return name.trim().toLowerCase().replaceAll(/\s+/g, " ");
}

/** Clasifica una unidad libre en su familia canónica + factor a la base. */
export function normalizeUnit(unit: string | null | undefined): NormalizedUnit {
  const raw = (unit ?? "").trim().toLowerCase().replace(/\.$/, "");
  const massFactor = MASS[raw];
  if (massFactor !== undefined) {
    return { family: "mass", factor: massFactor, raw };
  }
  const volumeFactor = VOLUME[raw];
  if (volumeFactor !== undefined) {
    return { family: "volume", factor: volumeFactor, raw };
  }
  if (SPOON_SMALL.has(raw)) {
    return { family: "spoon_small", factor: 1, raw };
  }
  if (SPOON_BIG.has(raw)) {
    return { family: "spoon_big", factor: 1, raw };
  }
  if (COUNT.has(raw)) {
    return { family: "count", factor: 1, raw };
  }
  // Unidad desconocida: familia "other" identificada por el texto exacto —
  // dos ingredientes con la misma unidad desconocida sí se suman entre sí.
  return { family: "other", factor: 1, raw };
}

/** Clave de familia para agregación (las "other" se separan por texto). */
export function unitFamilyKey(n: NormalizedUnit): string {
  return n.family === "other" ? `other:${n.raw}` : n.family;
}

const ROUND_DECIMALS = 2;
const ROUNDING = 10 ** ROUND_DECIMALS;
const KILO = 1000;

function round(value: number): number {
  return Math.round(value * ROUNDING) / ROUNDING;
}

export interface DisplayQuantity {
  quantity: number;
  unit: string | null;
}

/**
 * Presenta una cantidad en unidad base con la mejor unidad legible:
 * ≥1000 g → kg, ≥1000 ml → l; resto tal cual.
 */
export function toDisplayQuantity(
  baseQuantity: number,
  family: UnitFamily,
  rawUnit: string,
): DisplayQuantity {
  if (family === "mass") {
    return baseQuantity >= KILO
      ? { quantity: round(baseQuantity / KILO), unit: "kg" }
      : { quantity: round(baseQuantity), unit: "g" };
  }
  if (family === "volume") {
    return baseQuantity >= KILO
      ? { quantity: round(baseQuantity / KILO), unit: "l" }
      : { quantity: round(baseQuantity), unit: "ml" };
  }
  if (family === "spoon_small") {
    return { quantity: round(baseQuantity), unit: "cdta" };
  }
  if (family === "spoon_big") {
    return { quantity: round(baseQuantity), unit: "cda" };
  }
  if (family === "count") {
    return { quantity: round(baseQuantity), unit: rawUnit === "" ? null : "ud" };
  }
  return { quantity: round(baseQuantity), unit: rawUnit || null };
}
